import { useMemo } from 'react';
import { RotateCcw } from 'lucide-react';
import { SearchInput } from '@/components/ui/search-input';
import { Button } from '@/components/ui/button';
import type { PicklistValue } from '@/api/client';
import type { TicketFilters } from '@/api/hooks';
import { priorityDot, statusDot } from '@/lib/status-colors';
import { MultiSelect } from '@/components/MultiSelect';

interface Props {
  filters: TicketFilters;
  defaults: TicketFilters;
  onChange: (next: TicketFilters) => void;
  picklists?: { status: PicklistValue[]; priority: PicklistValue[]; queueID: PicklistValue[] };
  /** Show the "My tickets / All tickets" toggle. Hidden on unassigned-only views (Queues), where it would contradict the `unassigned` filter. */
  showAssignedToToggle?: boolean;
}

export function FilterBar({ filters, defaults, onChange, picklists, showAssignedToToggle = true }: Props) {
  const set = <K extends keyof TicketFilters>(k: K, v: TicketFilters[K]) => onChange({ ...filters, [k]: v });

  // Cheap-and-correct "is the user away from defaults?" check for small flat-ish objects.
  const isDefault = useMemo(() => JSON.stringify(filters) === JSON.stringify(defaults), [filters, defaults]);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <SearchInput
        withIcon
        placeholder="Search"
        className="w-64"
        value={filters.search}
        onValueChange={(v) => set('search', v)}
      />

      {showAssignedToToggle && (
        <Button
          variant={filters.assignedTo === 'me' ? 'default' : 'outline'}
          size="sm"
          onClick={() => set('assignedTo', filters.assignedTo === 'me' ? 'any' : 'me')}
        >
          {filters.assignedTo === 'me' ? 'My tickets' : 'All tickets'}
        </Button>
      )}

      {picklists && (
        <>
          <MultiSelect label="Status" options={picklists.status} selected={filters.status} onChange={(v) => set('status', v)} dot={statusDot} />
          <MultiSelect label="Priority" options={picklists.priority} selected={filters.priority} onChange={(v) => set('priority', v)} dot={(_, l) => priorityDot(l)} />
          <MultiSelect label="Queue" options={picklists.queueID} selected={filters.queueID} onChange={(v) => set('queueID', v)} />
        </>
      )}

      <Button
        variant="ghost"
        size="sm"
        onClick={() => onChange(defaults)}
        disabled={isDefault}
        title={isDefault ? 'Filters are at defaults' : 'Reset filters to this view\'s defaults'}
      >
        <RotateCcw className="mr-1 h-3 w-3" />Reset filters
      </Button>
    </div>
  );
}
